import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Form } from 'antd'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

import { WEEK } from 'constants/appointments'
import { Select, DatePicker, Modal, CheckboxGroup } from 'ui/styled'
import TimePicker from 'components/TimePicker'
import {
  disabledBeforeDate,
  DATEPICKER_FORMAT,
  TIMEPICKER_FORMAT
} from 'utils/helpers/dateHelper'
import { BLUE } from 'ui/constants'
import { Content } from './styled'
import { Time_Slot_Kind } from 'generated/types'

dayjs.extend(customParseFormat)

const { Item, useForm } = Form

const TimeSlotsModal = ({
  onCancel,
  onOk,
  visible = false,
  shouldBlockAsap = false
}) => {
  const [type, setType] = useState(null)
  const [date, setDate] = useState()
  const [form] = useForm()

  const newSelectOptions = [
    { label: 'ASAP', value: Time_Slot_Kind.Asap, disabled: shouldBlockAsap },
    { label: 'Time Block', value: Time_Slot_Kind.TimeBlock },
    { label: 'Specific Time', value: Time_Slot_Kind.Specific }
  ]

  const onFinish = values => {
    onOk(values)
    setType(null)
    form.resetFields()
  }

  const onChangeType = v => {
    setType(v)
    setDate()
    form.resetFields(['date', 'days', 's_time', 'e_time'])
  }

  const onChangeDate = v => {
    setDate(v)
    form.resetFields(['s_time', 'e_time'])
  }

  const onChangeStart = value => {
    form.setFieldValue('s_time', dayjs(value, TIMEPICKER_FORMAT))
    let newDate = date ? date.clone() : dayjs()
    const time = dayjs(value, TIMEPICKER_FORMAT)
    newDate = newDate.hour(time.hour()).minute(time.minute())

    setDate(newDate)
    form.resetFields(['e_time'])
  }

  const onChangeEnd = value => {
    form.setFieldValue('e_time', dayjs(value, TIMEPICKER_FORMAT))
  }

  const isASAP = type === 'asap'
  const isSpecific = type === 'specific'
  const isTimeBlock = type === 'time_block'
  const shouldBlockTimePicker = isSpecific && !date

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      title="Add Time Slot"
      onOk={form.submit}
      okButtonProps={{
        'data-test': 'addTimeSlotButton',
        style: {
          backgroundColor: BLUE,
          borderRadius: 6,
          borderColor: BLUE
        }
      }}
      cancelButtonProps={{
        'data-test': 'addTimeSlotCancelBtn',
        style: {
          borderRadius: 6
        }
      }}
    >
      <Content form={form} onFinish={onFinish}>
        <div>Request Time Type</div>
        <Item
          name="kind"
          rules={[
            {
              required: true,
              message: 'Required field'
            }
          ]}
        >
          <Select
            options={newSelectOptions}
            placeholder="Select Time Slot Type"
            onChange={onChangeType}
            data-test="timeSlotsModalSlotTypeSelect"
          />
        </Item>

        {isTimeBlock && (
          <Item
            name="days"
            rules={[
              {
                required: true,
                message: 'Required field'
              }
            ]}
          >
            <CheckboxGroup options={WEEK} direction="column" />
          </Item>
        )}

        {isSpecific && (
          <>
            <div>Date</div>
            <Item
              name="date"
              rules={[
                {
                  required: true,
                  message: 'Required field'
                }
              ]}
            >
              <DatePicker
                format={DATEPICKER_FORMAT}
                disabledDate={disabledBeforeDate}
                value={date}
                onChange={onChangeDate}
              />
            </Item>
          </>
        )}

        {!!type && !isASAP && (
          <>
            <Item
              label="Start Time"
              name="s_time"
              rules={[
                {
                  required: true,
                  message:
                    'Start Time is required and cannot be set in the past'
                }
              ]}
            >
              <TimePicker
                onSelect={onChangeStart}
                blockBefore={!isTimeBlock}
                disabled={shouldBlockTimePicker}
                startMoment={date}
                allowClear={false}
                assumeMidnight
                dataTest="timeSlotsModalStartTimeSelect"
              />
            </Item>

            <Item
              label="End Time"
              name="e_time"
              rules={[
                {
                  required: true,
                  message:
                    'End Time is required and cannot be set before start time'
                }
              ]}
            >
              <TimePicker
                onSelect={onChangeEnd}
                disabled={shouldBlockTimePicker}
                startMoment={date}
                allowClear={false}
                dataTest="timeSlotsModalEndTimeSelect"
              />
            </Item>
          </>
        )}
      </Content>
    </Modal>
  )
}

TimeSlotsModal.propTypes = {
  visible: PropTypes.bool,
  shouldBlockAsap: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onOk: PropTypes.func.isRequired
}

export default TimeSlotsModal
